<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import { routes } from '@app/router/routes'
import ListingRequestCard from '../components/list/ListingRequestCard.vue'
import { useListingRequestsQuery } from '../api/useListingRequestsQuery'

// Compact "recent requests" preview for the dashboard. Shows the latest few
// requests with a link to the full list; falls back to a first-time CTA.
const router = useRouter()

const PREVIEW_COUNT = 3
const query = useListingRequestsQuery()

const rows = computed(() =>
  (query.data.value?.pages.flatMap((page) => page.rows) ?? []).slice(0, PREVIEW_COUNT),
)
const isEmpty = computed(() => !query.isPending.value && rows.value.length === 0)

const SKELETON_ROWS = 2

function goToDetail(id: string) {
  void router.push(routes.LISTING_DETAIL.replace(':id', id))
}

function goToList() {
  void router.push(routes.LISTINGS)
}

function goToNew() {
  void router.push(routes.LISTING_NEW)
}
</script>

<template>
  <Box direction="col" gap="4" class="preview">
    <EmptyState
      v-if="isEmpty"
      icon="home"
      title="List your first property"
      description="Sliceal splits your property into tokens. Start a sell request and track it here."
      primary-action-label="List New Property"
      primary-action-icon="plus"
      @primary-action="goToNew"
    />

    <template v-else>
      <Box flex align="center" justify="between" gap="2">
        <Text is="h2" type="title-lg" weight="bold">Recent properties</Text>
        <Button
          label="View all"
          variant="text"
          icon="arrow-right"
          icon-pos="right"
          @click="goToList"
        />
      </Box>

      <Box v-if="query.isPending.value" direction="col" gap="3">
        <Skeleton v-for="index in SKELETON_ROWS" :key="index" height="88px" />
      </Box>

      <Box v-else direction="col" gap="3">
        <ListingRequestCard
          v-for="request in rows"
          :key="request.id"
          :request="request"
          @view="goToDetail"
        />
      </Box>
    </template>
  </Box>
</template>

<style scoped>
.preview {
  width: 100%;
}
</style>
